import { useCallback, useEffect, useState } from "react";

type StorageSetter = (newValue: string | null) => void;

export const useSessionStorage = (key: string): [string | null, StorageSetter] => {
  const [value, setValue] = useState(() => sessionStorage.getItem(key));
  const customEventName = `storage:session:${key}`;

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.storageArea === sessionStorage && event.key === key) {
        setValue(event.newValue);
      }
    };
    const handleSameDocumentStorageChange = () => {
      setValue(sessionStorage.getItem(key));
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(customEventName, handleSameDocumentStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(customEventName, handleSameDocumentStorageChange);
    };
  }, [customEventName, key]);

  const setStorage = useCallback((newValue: string | null) => {
    if (sessionStorage.getItem(key) === newValue) return;

    if (newValue === null) {
      sessionStorage.removeItem(key);
      setValue(null);
    } else {
      sessionStorage.setItem(key, newValue);
      setValue(newValue);
    }

    // Native storage events do not fire in the document that made the change.
    window.dispatchEvent(new Event(customEventName));
  }, [customEventName, key]);

  return [value, setStorage];
};
